<template>
  <modal
    name="zone-creation-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '80%'"
    :scrollable="true"
  >
    <!-- Modal Header -->
    <div
      class="pb-2 pt-4 pr-4 pl-4 border-b mb-4 border-gray-200 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 2xl:h-24 flex items-center justify-between"
    >
      <h4 class="text-xl font-semibold">Bölge Oluştur</h4>
      <button
        type="button"
        class="px-3 text-lg"
        @click="$modal.hide('zone-creation-modal')"
      >
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Loading Indicator -->
    <div v-if="isLoading" class="w-full h-96 flex items-center justify-center">
      <i class="fas fa-spinner fa-3x text-gray-400 fa-spin"></i>
    </div>

    <!-- Modal Content -->
    <div v-if="!isLoading" class="pb-4 px-4 pt-2">
      <div>
        <!-- Bölge Adı -->
        <div class="mb-4">
          <diffInput
            type="text"
            v-model="zoneData.name"
            title="Bölge Adı"
            :required="true"
            class="text-base"
            maxlength="255"
          />
        </div>
        
        <!-- Polygon Tipi -->
        <div class="mb-4">
          <diffInput
            v-model="selectedPolygonType"
            :optList="polygonTypes"
            :title="$t('operations.products.modal.type')"
            type="select"
            :required="true"
          />
        </div>
        
        <!-- Araç Sahiplik Filtreleri - İyileştirilmiş UI -->
        <div class="mb-6 bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <h3 class="text-gray-700 dark:text-gray-200 font-medium mb-3 flex items-center">
            <i class="fas fa-filter text-blue-500 mr-2"></i>
            Araç Sahiplik Filtreleme
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">
            Bu bölgede hangi tip araçların puanlamaya dahil edileceğini seçin.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
            <!-- Özmal Araçlar -->
            <div 
              class="flex items-center space-x-3 p-3 border rounded-lg transition-colors duration-200"
              :class="zoneData.countOzmal 
                ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
                : 'bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700'"
              @click="zoneData.countOzmal = !zoneData.countOzmal"
            >
              <div 
                class="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center"
                :class="zoneData.countOzmal 
                  ? 'bg-blue-500 border-blue-500 dark:bg-blue-600 dark:border-blue-600' 
                  : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'"
              >
                <i v-if="zoneData.countOzmal" class="fas fa-check text-white text-xs"></i>
              </div>
              <div>
                <label class="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer">Özmal Araçları Sayılsın</label>
                <p class="text-xs text-gray-500 dark:text-gray-400">Şirket bünyesindeki araçlar puanlamaya dahil edilsin</p>
              </div>
            </div>
            
            <!-- Kiralık Araçlar -->
            <div 
              class="flex items-center space-x-3 p-3 border rounded-lg transition-colors duration-200"
              :class="zoneData.countRental 
                ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' 
                : 'bg-gray-100 border-gray-200 dark:bg-gray-800 dark:border-gray-700'"
              @click="zoneData.countRental = !zoneData.countRental"
            >
              <div 
                class="flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center"
                :class="zoneData.countRental 
                  ? 'bg-blue-500 border-blue-500 dark:bg-blue-600 dark:border-blue-600' 
                  : 'bg-white border-gray-300 dark:bg-gray-700 dark:border-gray-600'"
              >
                <i v-if="zoneData.countRental" class="fas fa-check text-white text-xs"></i>
              </div>
              <div>
                <label class="text-sm font-medium text-gray-700 dark:text-gray-200 cursor-pointer">Kiralık Araçları Sayılsın</label>
                <p class="text-xs text-gray-500 dark:text-gray-400">Dış tedarikçilerden kiralanan araçlar puanlamaya dahil edilsin</p>
              </div>
            </div>
          </div>
          
          <!-- Filtre Özeti -->
          <div class="mt-3 p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm text-gray-600 dark:text-gray-300">
            <i class="fas fa-info-circle text-blue-500 mr-1"></i>
            <span v-if="zoneData.countOzmal && zoneData.countRental">
              Tüm araçlar bölge puanlamasına dahil edilecek.
            </span>
            <span v-else-if="zoneData.countOzmal">
              Sadece özmal araçlar bölge puanlamasına dahil edilecek.
            </span>
            <span v-else-if="zoneData.countRental">
              Sadece kiralık araçlar bölge puanlamasına dahil edilecek.
            </span>
            <span v-else class="text-red-500">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              Hiçbir araç türü seçilmedi! Lütfen en az bir araç türü seçin.
            </span>
          </div>
        </div>
        
        <!-- Location Selection -->
        <div class="mb-4">
          <gmapInputVue
            @change="handleLocationChange"
            :defaultAdress="zoneData.address"
            :lat="zoneData.latitude"
            :lng="zoneData.longitude"
            height="150px"
            placeHolder="Konum Seç"
            class="text-base"
            :hideMap="true"
          />
        </div>

        <!-- Map Component -->
        <div class="w-full mb-4" v-if="zoneData.latitude && zoneData.longitude">
          <zoneMap
            :polygon="zoneData.polyline"
            :lat="zoneData.latitude"
            :lng="zoneData.longitude"
            @handlePolygon="handlePolygon"
            @updateLocation="handleMarkerDrag"
            :height="'400px'"
          />
        </div>

        <!-- Footer -->
        <div class="flex justify-end space-x-2">
          <button
            type="button"
            @click="$modal.hide('zone-creation-modal')"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            İptal
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center justify-center min-w-[100px]"
            :class="{ 'opacity-50 cursor-not-allowed': loading || !isValid || (!zoneData.countOzmal && !zoneData.countRental) }"
            :disabled="loading || !isValid || (!zoneData.countOzmal && !zoneData.countRental)"
            @click="saveZone"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin mr-2"></i>
            <span v-else class="mr-2"><i class="fas fa-save"></i></span>
            Kaydet
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import axios from "axios";
import gmapInputVue from "@/components/general/gmapInput.vue";
import zoneMap from "./zoneMap.vue";
import { encode } from "@/utils/encoder";
import { routeZone } from "@/networking/urlmanager";
import diffInput from "@/components/general/diffInput.vue";
export default {
  name: "ZoneCreationModal",
  components: {
    gmapInputVue,
    zoneMap,
    diffInput,
  },
  data() {
    return {
      isLoading: false,
      loading: false,
      zoneData: {
        name: "",
        address: "",
        latitude: "",
        longitude: "",
        polyline: [],
        countOzmal: true, // Default olarak özmal araçlar sayılsın
        countRental: true, // Default olarak kiralık araçlar sayılsın
      },
      selectedPolygonType:null,
      polygonTypes:[{id:1,name:"Bekleme Noktası"},{id:2,name:"Yıkama Noktası"},{id:3,name:"Sevkiyat Noktası"}],

    };
  },
  computed: {
    isValid() {
      return (
        this.selectedPolygonType &&
        this.zoneData.address &&
        this.zoneData.name &&
        this.zoneData.latitude &&
        this.zoneData.longitude &&
        this.zoneData.polyline.length > 0
      );
    },
    isMobile() {
      return window.innerWidth <= 768;
    }
  },
  methods: {
    handleLocationChange(location) {
      this.zoneData.address = location.adress;
      this.zoneData.latitude = location.lat;
      this.zoneData.longitude = location.lng;
    },
    handleMarkerDrag(location) {
      this.zoneData.latitude = location.lat;
      this.zoneData.longitude = location.lng;
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: location.lat, lng: location.lng } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            this.zoneData.address = results[0].formatted_address;
          }
        }
      );
    },
    handlePolygon(coordinates) {
      const polyline = coordinates.map((coord) => [coord.lat, coord.lng]);
      this.zoneData.polyline = encode({ polyline });
    },
    async saveZone() {
      if (!this.isValid) {
        this.$swal.fire({
          icon: "warning",
          title: "Uyarı",
          text: "Lütfen tüm alanları doldurun ve haritadan bölge seçin",
          confirmButtonText: "Tamam",
        });
        return;
      }
      
      if (!this.zoneData.countOzmal && !this.zoneData.countRental) {
        this.$swal.fire({
          icon: "warning",
          title: "Uyarı",
          text: "Lütfen en az bir araç türü seçin (Özmal veya Kiralık)",
          confirmButtonText: "Tamam",
        });
        return;
      }

      if (this.zoneData.polyline === "BF") {
        this.swalBox(
          "warning",
          "UYARI",
          "Bölge çizmeden kaydedemezsiniz.",
          false,
          this.$t("general.OkayTitle")
        );
        return;
      }

      this.loading = true;
      try {
        await axios.post(routeZone.add, {
          ...this.zoneData, 
          polygonType: this.selectedPolygonType,
          // Boolean'dan sayıya çevir
          countOzmal: this.zoneData.countOzmal ? 1 : 0,
          countRental: this.zoneData.countRental ? 1 : 0
        }, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        await this.$swal.fire({
          icon: "success",
          title: this.$t("general.successTitle"),
          confirmButtonText: this.$t("general.OkayTitle"),
        });

        this.$emit("refresh", true);
        this.$modal.hide("zone-creation-modal");
      } catch (error) {
        this.errorBox(error.response);
      } finally {
        this.loading = false;
        this.$emit("refresh", true);
        this.zoneData= {
          name: "",
          address: "",
          latitude: "",
          longitude: "",
          polyline: [],
          countOzmal: true,
          countRental: true,
        };
      }
    },
    resetForm() {
      this.zoneData = {
        address: "",
        latitude: "",
        longitude: "",
        polyline: [],
        countOzmal: true,
        countRental: true,
      };
    },
  },
};
</script>
